import { createTelemetryObject, createTelemetry } from '@shared/core/analytics';
import { TravelMapBlockType } from './Accommodations.types';

const category = 'website';
const pagePrefix = 'admin';
const action = 'TravelButtonInteracted';
const addAccommodationItemLabel = 'Create Stay';
const addAccommodationItemCategory = 'admin.accommodations';

const actionProps = {
  action,
  category,
  pagePrefix,
};

const telemObject = createTelemetryObject({
  pages: {
    aiRecommendationsFeedbackDialog: () => ({
      category,
      pagePrefix,
      page: 'aiRecommendationsFeedbackDialog',
    }),
    onboardingWelcome: (tourAction: 'Exited Tour' | 'Tour Seen' | 'Tour Relaunched') => ({
      category: 'Tour',
      pagePrefix: 'admin',
      page: 'onboarding-accommodations',
      extraPageInfo: {
        tourName: 'accommodations',
        stepName: 'Intro',
        stepNumber: 0,
        tourAction,
      },
    }),
  },
  actions: {
    updateBlockOrdering: (page: string, args: { blockOrdering: ReadonlyArray<string>; isAccommodation?: boolean }) => ({
      ...actionProps,
      page,
      label: 'Update Block Ordering',
      extraInfo: {
        ...args,
      },
    }),
    manageYourHotels: (page: string) => ({
      ...actionProps,
      page,
      label: 'Manage Your Hotels',
    }),
    comingSoonCtaClicked: (page: string) => ({
      ...actionProps,
      page,
      label: 'Coming Soon CTA clicked',
    }),
    pitchCtaClicked: (page: string) => ({
      ...actionProps,
      page,
      label: 'Pitch CTA clicked',
    }),
    inProgressCtaClicked: (page: string) => ({
      ...actionProps,
      page,
      label: 'In Progress CTA clicked',
    }),
    getAccommodationsCtaClicked: (page: string) => ({
      ...actionProps,
      page,
      label: 'Get Accommodations CTA clicked',
    }),
    contactConciergeCtaClicked: (page: string) => ({
      ...actionProps,
      page,
      label: 'Contact Concierge CTA clicked',
    }),
    manageRoomblocksCtaClicked: (page: string) => ({
      ...actionProps,
      page,
      label: 'Manage Roomblocks CTA clicked',
    }),
    deleteItemDialog: (page: string, args: { itemType: TravelMapBlockType; isSuperAdmin?: boolean }) => ({
      ...actionProps,
      page,
      label: 'Delete Item Dialog',
      extraInfo: {
        ...args,
      },
    }),
    deleteItemDialogDismiss: (page: string, args: { itemType: TravelMapBlockType; isSuperAdmin: boolean }) => ({
      ...actionProps,
      page,
      label: 'Delete Item Dialog Dismiss',
      extraInfo: {
        ...args,
      },
    }),
    deleteItemSubmit: (page: string, args: { itemType: TravelMapBlockType; isSuperAdmin: boolean; createdByType?: string }) => ({
      action,
      category: 'admin.accommodations',
      pagePrefix,
      page,
      label: 'Delete Item Submit',
      extraInfo: {
        ...args,
      },
    }),
    cancelRoomBlock: (page: string, args: { roomBlockId: string; isSuperAdmin: boolean; website: string; datetime: string }) => ({
      ...actionProps,
      page,
      label: 'Cancel Room Block',
      extraInfo: {
        ...args,
      },
    }),
    cancelRoomBlockConfirm: (
      page: string,
      args: { roomBlockId: string; cancellationReason: string; isSuperAdmin: boolean; website: string; datetime: string; hotelName: string },
    ) => ({
      ...actionProps,
      page,
      label: 'Cancel Room Block Confirmed',
      extraInfo: {
        ...args,
      },
    }),
    addItemClicked: (page: string, args: { itemType: TravelMapBlockType; isSuperAdmin?: boolean }) => ({
      ...actionProps,
      page,
      label: 'Add Item Clicked',
      extraInfo: {
        ...args,
      },
    }),
    addAccommodationClicked: (page: string, args: { isSuperAdmin?: boolean; category: string }) => ({
      ...actionProps,
      page,
      label: 'Add Accommodations',
      extraInfo: {
        ...args,
      },
    }),
    addAccommodationItemClicked: (page: string, args: { itemType?: TravelMapBlockType; label?: string; source?: string }) => ({
      ...actionProps,
      page,
      category: addAccommodationItemCategory,
      extraInfo: {
        itemType: args.itemType,
        label: args.label || addAccommodationItemLabel,
        accommodationAction: 'Start Adding Stay',
        source: args.source ?? 'addAccommodations',
      },
    }),
    submitFeedback: (page: string, args: { feedbackType: 'positive' | 'neutral' | 'negative'; feedbackText: string }) => ({
      ...actionProps,
      page,
      label: 'Submit Feedback',
      extraInfo: {
        ...args,
      },
    }),
    addItemSubmit: (page: string, args: { itemType: TravelMapBlockType; isSuperAdmin?: boolean }) => ({
      ...actionProps,
      page,
      label: 'Add Item Submit',
      extraInfo: {
        ...args,
      },
    }),
    addItemCancel: (page: string, args: { itemType: TravelMapBlockType; isSuperAdmin?: boolean }) => ({
      ...actionProps,
      page,
      label: 'Add Item Cancel',
      extraInfo: {
        ...args,
      },
    }),
    editItemClicked: (
      page: string,
      args: {
        itemType: TravelMapBlockType;
        isSuperAdmin?: boolean;
        zhId?: string | null;
        displayName?: string | null;
        customName?: string | null;
        url?: string | null;
        customUrl?: string | null;
        isMessageShowing?: boolean;
        accommodationAction?: 'accommodationEdit' | 'accommodationSave';
      },
    ) => ({
      ...actionProps,
      page,
      label: 'Edit Item Clicked',
      extraInfo: {
        ...args,
      },
    }),
    editItemSubmit: (page: string, args: { itemType: TravelMapBlockType; isSuperAdmin?: boolean }) => ({
      ...actionProps,
      page,
      label: 'Edit Item Submit',
      extraInfo: {
        ...args,
      },
    }),
    editItemCancel: (page: string, args: { itemType: TravelMapBlockType; isSuperAdmin: boolean }) => ({
      ...actionProps,
      page,
      label: 'Edit Item Cancel',
      extraInfo: {
        ...args,
      },
    }),
    toggleExploreCTA: (page: string, args: { isVisible: boolean }) => ({
      ...actionProps,
      page,
      extraInfo: {
        name: 'accommodationsToggleExploreCTA',
        ...args,
      },
    }),
    removeOurPicks: (page: string) => ({
      ...actionProps,
      page,
      extraInfo: {
        name: 'removeOurPicks',
      },
    }),
    saveButtonClicked: (
      page: string,
      args: {
        itemType: TravelMapBlockType;
        isSuperAdmin?: boolean;
        zhId?: string | null;
        accommodationAction?: 'added' | 'edited';
        usingCustomLink?: boolean;
        customLink?: string;
      },
    ) => ({
      ...actionProps,
      page,
      category: 'admin.accommodations',
      label: 'Save',
      extraInfo: {
        ...args,
      },
    }),
    deleteAccommodationPlaceDialog: (page: string, args: { itemType: TravelMapBlockType; isSuperAdmin?: boolean; createdByType?: Maybe<string> }) => ({
      action,
      category: 'admin.accommodations',
      pagePrefix,
      page,
      label: 'Delete Item Dialog',
      extraInfo: {
        ...args,
      },
    }),
    aiRecommendationsFeedbackDialogSubmit: (feedbackRating: number, algoTag?: Maybe<string>, feedbackMessage?: string) => ({
      ...actionProps,
      extraInfo: {
        name: 'aiRecommendationsFeedbackDialogSubmit',
        feedbackRating,
        algoTag,
        feedbackMessage,
      },
    }),
    aiRecommendationsFeedbackDialogRemindMeLater: () => ({
      ...actionProps,
      extraInfo: {
        name: 'aiRecommendationsFeedbackDialogRemindMeLater',
      },
    }),
    roomBlockRatesDialogShowHotelPicks: () => ({
      ...actionProps,
      extraInfo: {
        name: 'roomBlockRatesDialogShowHotelPicks',
      },
    }),
  },
});

const { TelemetryProvider: AdminAccommodationsTelemetryProvider, useTelemetry: useAdminAccommodationsTelemetry } = createTelemetry(telemObject);

export { AdminAccommodationsTelemetryProvider, useAdminAccommodationsTelemetry };
